import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Icon from 'components/Icon';
import Spinner from 'components/Spinner';
import Heading from 'components/Heading';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useChatControl } from 'libs/rendering/hooks';
import { UiPosition } from 'libs/rendering/gltf/config';

import {
  SIZE_CONFIG,
  RightClickMenuWrapper,
  RowWrapper,
  BlockWrapper,
  LabelBlockWrapper,
  SubmenuWrapper,
  RowDivider,
} from 'components/RightClickMenu/RightClickMenu.styles';

export default function RightClickMenu(props) {
  const viewerControl = useViewerControl();
  const chatControl = useChatControl();

  const { options, isDark } = props;

  const ref = useRef(null);
  const [show, setShow] = useState(false);
  const [point, setPoint] = useState({
    x: null,
    y: null,
    invertSubmenuX: false,
    invertSubmenuY: false,
  });
  const [submenuOpen, setSubmenuOpen] = useState([null]);

  const offsetRight = () => {
    if (chatControl?.chatVisible) {
      return UiPosition.panel;
    }
    return 0;
  };

  const onOpen = (x, y) => {
    // bug: at this stage, options is always empty so cannot calculate the menu height
    const size = {
      width: SIZE_CONFIG?.width || 216,
      height:
        SIZE_CONFIG?.square && options?.length
          ? SIZE_CONFIG?.square * options.length
          : 32,
      screenWidth:
        window?.innerWidth - offsetRight() ||
        document?.documentElement?.clientWidth - offsetRight(),
      screenHeight:
        window?.innerHeight || document?.documentElement?.clientHeight,
    };

    setPoint({
      x:
        x + size.width > size.screenWidth
          ? x - size.width + SIZE_CONFIG.inwards
          : x - SIZE_CONFIG.inwards,
      y:
        y + size.height > size.screenHeight
          ? y - size.height + SIZE_CONFIG.inwards
          : y - SIZE_CONFIG.inwards,
      invertSubmenuX: size.width * 2 + x > size.screenWidth,
      invertSubmenuY: size.height * 2 + y > size.screenHeight,
    });
    setShow(true);
  };

  useEffect(() => {
    const handleContextMenu = event => {
      if (viewerControl.isPreview || !viewerControl.viewerVisible) return;
      const wrapperHolder = document.getElementById(viewerControl.wrapperId);
      if (wrapperHolder.contains(event.target)) {
        event?.preventDefault();
        onOpen(event?.clientX, event?.clientY);
      }
    };

    const handleClickOutside = event => {
      if (!show && ref?.current && !ref?.current?.contains(event?.target)) {
        setShow(false);
        viewerControl.CloseContextMenu();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('click', handleClickOutside, true);
      viewerControl.CloseContextMenu();
    };
  }, []);

  const updateSubmenuOpen = (e, level, index, obj) => {
    const currentState = [...submenuOpen];
    currentState[level] = index;
    setSubmenuOpen(currentState);

    if (obj) {
      const { onMouseEnter } = obj;
      if (onMouseEnter) onMouseEnter();
    }

    e.stopPropagation();
  };

  const resetSubmenuOpen = obj => {
    setSubmenuOpen([null]);

    if (obj) {
      const { onMouseLeave } = obj;
      if (onMouseLeave) onMouseLeave();
    } else {
      viewerControl.CloseContextMenu();
    }
  };

  const click = (event, obj) => {
    event?.stopPropagation();

    // not very sure what is this for,
    // each option has its own onclick...
    const { onRowClick } = props;
    if (onRowClick) onRowClick(obj);

    const { onClick, closeAfter } = obj;
    if (onClick) onClick();
    if (closeAfter) {
      setShow(false);
      viewerControl.CloseContextMenu();
    }
  };

  const optionsBuilder = (list, level = 0) =>
    list.map((obj, index) => {
      const {
        loading,
        icon,
        viewBox,
        iconLast,
        label,
        extraLabel,
        isTitle,
        onClick,
        onMouseEnter,
        autoWidth,
        transparent,
        highlight,
      } = obj;
      const submenuOptions = obj?.options;
      const hasIconBlock = loading || icon;
      const hasSubmenu =
        Array.isArray(submenuOptions) && submenuOptions?.length !== 0;
      const subMenuAutoWidth = hasSubmenu && submenuOptions[0].autoWidth;
      const hasAction = hasSubmenu || onClick != null || onMouseEnter != null;
      return (
        <RowWrapper
          key={index}
          isDark={isDark}
          isTitle={isTitle}
          hasAction={hasAction}
          autoWidth={autoWidth}
          transparent={transparent}
          highlight={highlight}
          onClick={e => click(e, obj)}
          onMouseEnter={e => updateSubmenuOpen(e, level, index, obj)}
        >
          {!isDark && !transparent && list.length - 1 !== index && (
            <RowDivider style={{ width: '100%' }} />
          )}
          {hasIconBlock && (
            <BlockWrapper last={iconLast}>
              {loading ? (
                <Spinner isDark={isDark} size='small' />
              ) : (
                <Icon name={icon} viewBox={viewBox} />
              )}
            </BlockWrapper>
          )}
          <LabelBlockWrapper
            hasIconBlock={hasIconBlock && !iconLast}
            autoWidth={autoWidth}
          >
            <Heading
              singleLine
              level='5'
              size='small'
              weight={isTitle || highlight ? 'xbold' : 'regular'}
              variant='primary'
            >
              {loading ? obj?.loadLabel || label : label}
            </Heading>
          </LabelBlockWrapper>
          {extraLabel && (
            <LabelBlockWrapper extra>
              <Heading
                singleLine
                level='5'
                size='small'
                weight='regular'
                variant={!transparent ? 'minimal' : 'primary'}
              >
                <span>{extraLabel}</span>
              </Heading>
            </LabelBlockWrapper>
          )}
          {hasSubmenu && (
            <BlockWrapper>
              <Icon name='caret_right' />
            </BlockWrapper>
          )}
          {hasSubmenu && submenuOpen?.[level] === index && (
            <SubmenuWrapper
              isDark={isDark}
              invertSubmenuX={!subMenuAutoWidth && point?.invertSubmenuX}
              invertSubmenuY={point?.invertSubmenuY}
              submenuOptionsLength={submenuOptions?.length}
              onMouseEnter={e => updateSubmenuOpen(e, level, index, obj)}
              onMouseLeave={() => resetSubmenuOpen(obj)}
            >
              {optionsBuilder(submenuOptions, level + 1)}
            </SubmenuWrapper>
          )}
        </RowWrapper>
      );
    });

  if (!show || point?.x === null || point?.y === null) return null;
  if (!Array.isArray(options) || options?.length === 0) return null;

  return (
    <RightClickMenuWrapper
      ref={ref}
      isDark={isDark}
      left={point.x}
      top={point.y}
      onMouseLeave={() => resetSubmenuOpen(null)}
      onContextMenu={e => e.preventDefault()}
    >
      {optionsBuilder(options)}
    </RightClickMenuWrapper>
  );
}

RightClickMenu.defaultProps = {
  isDark: false,
  options: [],
};

RightClickMenu.propTypes = {
  /**
   * When it is on it inverts the main colors
   */
  isDark: PropTypes.bool,
  /**
   * List of objects for each option.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool,
      loadLabel: PropTypes.string,
      icon: PropTypes.string,
      iconLast: PropTypes.bool,
      label: PropTypes.string,
      isTitle: PropTypes.bool,
      extraLabel: PropTypes.string,
      onClick: PropTypes.func,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          loading: PropTypes.bool,
          loadLabel: PropTypes.string,
          icon: PropTypes.string,
          iconLast: PropTypes.bool,
          label: PropTypes.string,
          extraLabel: PropTypes.string,
          onClick: PropTypes.func,
          options: PropTypes.arrayOf(PropTypes.object),
        }),
      ),
    }),
  ),
  /**
   * When the user clicks a row option this function outputs the selected object
   */
  onRowClick: PropTypes.func,
};
